/* eslint-disable react/display-name */
import React, { useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { transformToNewline } from '../../utils/stringManipulations'
import DataTable from '../DataTable'
import orderBy from 'lodash/orderBy'
import CustomLoader from '../Loader/CustomLoader'
import { subscribePlaceOrder, orderCount } from '../../apollo'
import { useQuery, gql } from '@apollo/client'
import SearchBar from '../TableHeader/SearchBar'
import { customStyles } from '../../utils/tableCustomStyles'
import TableHeader from '../TableHeader'
import { Box } from '@mui/material'
import StatusBadge from '../StatusBadge'

const ORDERCOUNT = gql`
  ${orderCount}
`
const ORDER_PLACED = gql`
  ${subscribePlaceOrder}
`

const OrdersData = props => {
  const { t } = props
  const { selected, updateSelected, searchQuery = '', onSearchChange } = props
  const onChangeSearch = e => {
    if (typeof onSearchChange === 'function') onSearchChange(e.target.value)
  }
  const getItems = items => {
    return items
      .map(
        item =>
          `${item.quantity}x${item.title}${
            item.variation.title ? `(${item.variation.title})` : ''
          }`
      )
      .join('\n')
  }
  const renderVoucher = coupon => {
    if (!coupon?.title) return '—'
    if (!coupon.discount) return coupon.title
    const amount =
      coupon.discountType === 'PERCENTAGE'
        ? `${coupon.discount}%`
        : `£${coupon.discount}`
    return `${coupon.title} · ${amount}`
  }
  const restaurantId = localStorage.getItem('restaurantId')

  // Counted with the same search filter as the rows, so "1-10 of N" stays honest
  const { data, loading: loadingQuery } = useQuery(ORDERCOUNT, {
    variables: { restaurant: restaurantId, search: props.search || '' }
  })

  const propExists = (obj, path) => {
    return path.split('.').reduce((obj, prop) => {
      return obj && obj[prop] ? obj[prop] : ''
    }, obj)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (field && isNaN(propExists(row, field))) {
        return propExists(row, field).toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const handlePerRowsChange = (perPage, page) => {
    props.page(page)
    props.rows(perPage)
  }

  const handlePageChange = async page => {
    props.page(page)
  }

  const columns = [
    {
      name: t('OrderID'),
      sortable: true,
      selector: 'orderId',
      cell: row => (
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          #{row.orderId}
        </Box>
      )
    },
    {
      name: t('Customer'),
      selector: 'user.name',
      sortable: true,
      cell: row => <>{row.user?.name || '—'}</>
    },
    {
      name: t('Phone'),
      cell: row => <>{row.user?.phone || '—'}</>
    },
    {
      name: t('Items'),
      cell: row => <>{getItems(row.items)}</>
    },
    {
      name: t('Payment'),
      selector: 'paymentMethod',
      sortable: true
    },
    {
      name: t('Voucher'),
      sortable: true,
      selector: 'coupon.title',
      cell: row => <>{renderVoucher(row.coupon)}</>
    },
    {
      name: t('Status'),
      selector: 'orderStatus',
      sortable: true,
      cell: row => <StatusBadge status={row.orderStatus} />
    },
    {
      name: t('Datetime'),
      cell: row => (
        <>{new Date(row.createdAt).toLocaleString().replace(/ /g, '\n')}</>
      )
    },
    {
      name: t('Address'),
      cell: row => (
        <>{transformToNewline(row.deliveryAddress.deliveryAddress, 3)}</>
      )
    }
  ]

  const conditionalRowStyles = [
    {
      when: row =>
        row.orderStatus !== 'DELIVERED' && row.orderStatus !== 'CANCELLED',
      style: {
        // Open orders get a faint warm wash so they stand out from history.
        backgroundColor: '#FFF8F3'
      }
    }
  ]
  useEffect(() => {
    props.subscribeToMore({
      document: ORDER_PLACED,
      variables: { id: restaurantId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        if (subscriptionData.data.subscribePlaceOrder.origin === 'new') {
          return {
            ordersByRestId: [
              subscriptionData.data.subscribePlaceOrder.order,
              ...prev.ordersByRestId
            ]
          }
        } else {
          const orderIndex = prev.ordersByRestId.findIndex(
            o => subscriptionData.data.subscribePlaceOrder.order._id === o._id
          )
          prev.ordersByRestId[orderIndex] =
            subscriptionData.data.subscribePlaceOrder.order
          return { ordersByRestId: [...prev.ordersByRestId] }
        }
      },
      onError: error => {
        console.log('onError', error)
      }
    })
  }, [])
  useEffect(() => {
    if (selected) {
      const order = props.orders.find(o => o._id === selected._id)
      updateSelected(order)
    }
  }, [props.orders])

  return (
    <>
      <DataTable
        title={<TableHeader title={t('Orders')} />}
        columns={columns}
        data={props.orders}
        onRowClicked={props.toggleModal}
        progressPending={props.loading || loadingQuery}
        pointerOnHover
        progressComponent={<CustomLoader />}
        sortFunction={customSort}
        subHeader
        subHeaderComponent={
          <SearchBar
            value={searchQuery}
            onChange={onChangeSearch}
            placeholder={t('SearchOrderOrVoucher')}
          />
        }
        pagination
        paginationServer
        paginationDefaultPage={props.currentPage || 1}
        paginationTotalRows={data && data.orderCount}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        conditionalRowStyles={conditionalRowStyles}
        customStyles={customStyles}
        selectableRows
        paginationIconLastPage=""
        paginationIconFirstPage=""
      />
    </>
  )
}
export default withTranslation()(OrdersData)
